import { IconButton, Menu, MenuItem } from '@mui/material';
import { FC, JSX, useState } from 'react';
import MenuIcon from '@mui/icons-material/MoreVert';

export type MenuOption = {
  label: string;
  onClick: (id?: string) => void;
};

type MenuProps = {
  options: MenuOption[];
  closeOnItemClick?: boolean;
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  id?: string;
  menuIcon?: JSX.Element;
};

export const MenuButton: FC<MenuProps> = ({
  options,
  closeOnItemClick = true,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  id,
  menuIcon
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleItemClick = (onClick: (id?: string) => void) => {
    if (closeOnItemClick) {
      setMenuAnchor(null);
    }
    onClick(id);
  };

  return (
    <>
      <IconButton
        aria-label="menu"
        onClick={handleMenuOpen}
        sx={{
          border: '1px solid var(--ds-button-secondary-border)',
          background: 'var(--ds-button-secondary-gradient)',
          color: 'var(--ds-button-secondary-text)',
          '&:hover': {
            borderColor: 'var(--ds-button-secondary-border-hover)',
            background: 'var(--ds-button-secondary-gradient-hover)'
          }
        }}
      >
        {menuIcon || <MenuIcon />}
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.8,
            borderRadius: 2,
            border: (theme) =>
              `1px solid ${theme.designSystem.surface.headerDivider}`,
            backgroundColor: (theme) => theme.designSystem.surface.panelBackground,
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }
        }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => handleItemClick(option.onClick)}
            sx={{
              borderRadius: 1.2,
              mx: 0.6,
              my: 0.3,
              '&:hover': {
                backgroundColor: 'var(--ds-button-secondary-bg-hover)'
              }
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
